'use client';

import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as turf from '@turf/turf';
import maplibregl from 'maplibre-gl';
import { CatchmentFormValues } from '@/components/catchmentform/CatchmentForm';
import { useCatchmentQuery } from '@/libs/catchment-api';
import { getRainfallEventArea } from '@/libs/rainfall-event-api';
import { fetchSuggestedSudsData } from '@/libs/suds-data-fetch';
import { getMultiPolygon } from '@/utils/catchment';
import {
  clearCatchmentSource,
  clearRainfallEventBoundarySource,
  fitBounds,
  initialiseCatchmentLayers,
  initialiseCatchmentSource,
  initialiseRainfallEventBoundaryLayer,
  initialiseRainfallEventBoundarySource,
  initialiseRainfallImageLayers,
  initialiseRunoffImageLayers,
  updateRainfallImage,
  updateRunoffImage,
} from '@/utils/map';
import { useMapFilterContext } from './MapFilterContext';
import { useSuggestedSudsContext } from './SuggestedSudsContext';

const AWS_API_KEY = process.env.NEXT_PUBLIC_AWS_LOCATION_SERVICES_API_KEY ?? '';
const AWS_REGION = process.env.NEXT_PUBLIC_REGION;

// TODO: Make dynamic when integrated with real data
export const steps = ['20/02', '21/02', '22/02', '23/02', '24/02', '25/02', '26/02', '27/02'];

type Catchment = CatchmentFormValues;

type MapContextType = {
  map: maplibregl.Map | null;
  mapContainer: React.RefObject<HTMLDivElement | null>;
  catchment: Catchment | null;
  simulationSelection: string | null;
  isSimulationPlaying: boolean;
  simulationSliderValue: number;
  isCatchmentLoading: boolean;
  isCatchmentSuccess: boolean;
  isOptimalMode: boolean;
  isOptimalModeLoaded: boolean;
  mapFilterPolygon: GeoJSON.Feature<GeoJSON.MultiPolygon> | null;
  showRainfallLayer: boolean;
  activeCsoEvents: number[];
  setCatchment: React.Dispatch<React.SetStateAction<Catchment | null>>;
  setSimulationSelection: React.Dispatch<React.SetStateAction<string | null>>;
  setIsSimulationPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  setSimulationSliderValue: React.Dispatch<React.SetStateAction<number>>;
  playSimulation: () => void;
  onSimulationStepChange: (step: number) => void;
  findOptimalLocations: () => void;
  setIsOptimalModeLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  exitOptimalMode: () => void;
  setShowRainfallLayer: React.Dispatch<React.SetStateAction<boolean>>;
};

export const MapContext = createContext<MapContextType | undefined>(undefined);

type ScenarioCsoEvent = {
  csoId: number;
  day: string;
};

type MapContextProviderProps = {
  children: ReactNode;
};

export function MapContextProvider({ children }: MapContextProviderProps) {
  const { showAreaBoundary } = useMapFilterContext();
  const { setSuggestedSudsData, clearDrawer } = useSuggestedSudsContext();

  const mapContainer = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<maplibregl.Map | null>(null);

  const [catchment, setCatchment] = useState<null | Catchment>(null);

  const [simulationSelection, setSimulationSelection] = useState<string | null>(null);
  const [isSimulationPlaying, setIsSimulationPlaying] = useState<boolean>(false);
  const [simulationSliderValue, setSimulationSliderValue] = useState<number>(0);
  const [isOptimalMode, setIsOptimalMode] = useState<boolean>(false);
  const [isOptimalModeLoaded, setIsOptimalModeLoaded] = useState<boolean>(false);

  const [viewMode, setViewMode] = useState<'catchment' | 'simulation'>('catchment');
  const [mapFilterPolygon, setMapFilterPolygon] =
    useState<GeoJSON.Feature<GeoJSON.MultiPolygon> | null>(null);

  const [scenarioCsoEvents, setScenarioCsoEvents] = useState<ScenarioCsoEvent[]>([]);
  const [activeCsoEvents, setActiveCsoEvents] = useState<number[]>([]);
  const [showRainfallLayer, setShowRainfallLayer] = useState<boolean>(true);
  const [stepDay, setStepDay] = useState<string | null>(null);

  useEffect(() => {
    if (!mapContainer.current) {
      return;
    }

    const initializedMap = new maplibregl.Map({
      container: mapContainer.current,
      style: `https://maps.geo.${AWS_REGION}.amazonaws.com/v2/styles/Standard/descriptor?key=${AWS_API_KEY}`,
      center: [-1.4669479, 52.5650073],
      zoom: 6,
      pitch: 0,
      bearing: 0,
    });

    initializedMap.on('load', () => {
      initialiseCatchmentSource(initializedMap, 'catchment');
      initialiseCatchmentLayers(initializedMap, 'catchment-fill', 'catchment-outline');
      initialiseRainfallEventBoundarySource(initializedMap, 'rainfall-event-area');
      initialiseRainfallEventBoundaryLayer(initializedMap, 'rainfall-event-outline');
      initialiseRainfallImageLayers(initializedMap);
      initialiseRunoffImageLayers(initializedMap);
    });

    setMap(initializedMap);

    return () => initializedMap.remove();
  }, []);

  const {
    data: catchmentGeojson,
    isLoading: isCatchmentLoading,
    isSuccess: isCatchmentSuccess,
  } = useCatchmentQuery(catchment);

  useEffect(() => {
    if (map && map.getSource('catchment')) {
      const source = map.getSource('catchment') as maplibregl.GeoJSONSource;
      if (catchmentGeojson) {
        const filteredCatchment = {
          ...catchmentGeojson,
          features: (catchmentGeojson.features as GeoJSON.Feature[]).filter((feature) =>
            ['Polygon', 'MultiPolygon'].includes(feature.geometry.type)
          ),
        };

        source.setData(filteredCatchment);
        fitBounds(map, filteredCatchment);
      } else {
        clearCatchmentSource(source);
      }
    }
  }, [map, catchmentGeojson]);

  const catchmentPolygon: GeoJSON.Feature<GeoJSON.MultiPolygon> | null = useMemo(() => {
    if (catchmentGeojson) {
      return getMultiPolygon(catchmentGeojson);
    }
    return null;
  }, [catchmentGeojson]);

  const rainfallEventGeojson = useMemo(() => {
    if (simulationSelection) {
      return getRainfallEventArea();
    }
    return null;
  }, [simulationSelection]);

  const rainfallEventPolygon = useMemo(() => {
    if (rainfallEventGeojson) {
      const catchmentGeometries = rainfallEventGeojson.geometries as GeoJSON.Geometry[];

      const coordinates = catchmentGeometries.flatMap((geometry) => {
        if (geometry.type === 'Polygon') {
          return [geometry.coordinates];
        } else if (geometry.type === 'MultiPolygon') {
          return geometry.coordinates;
        }
        return null;
      }) as GeoJSON.Position[][][];

      return turf.multiPolygon(coordinates);
    }
    return null;
  }, [rainfallEventGeojson]);

  useEffect(() => {
    if (map && simulationSelection) {
      const preloadFirstImage = async () => {
        try {
          const day = steps[0].split('/')[0];
          const imageUrl = `https://suds.slalomdev.io/mapdata/acc_rainfall_${day}feb.png`;

          const img = new Image();
          img.src = imageUrl;

          await new Promise((resolve) => {
            img.onload = resolve;
          });

          steps.slice(1).forEach((step) => {
            const day = step.split('/')[0];
            const img = new Image();
            img.src = `https://suds.slalomdev.io/mapdata/acc_rainfall_${day}feb.png`;
          });
        } catch (error) {
          console.error('Error preloading images:', error);
        }
      };

      preloadFirstImage();
    }
  }, [map, simulationSelection]);

  useEffect(() => {
    if (viewMode === 'catchment') {
      setMapFilterPolygon(catchmentPolygon);
    } else if (viewMode === 'simulation') {
      setMapFilterPolygon(rainfallEventPolygon);
    } else {
      setMapFilterPolygon(null);
    }
  }, [viewMode, catchmentPolygon, rainfallEventPolygon]);

  useEffect(() => {
    const visibility = showAreaBoundary ? 'visible' : 'none';

    if (map) {
      map.setLayoutProperty('catchment-fill', 'visibility', visibility);
      map.setLayoutProperty('catchment-outline', 'visibility', visibility);
    }
  }, [showAreaBoundary]);

  const playSimulation = async () => {
    setViewMode('simulation');
    setIsSimulationPlaying(true);

    if (map) {
      // layer 1 should be invisble
      if (map.getLayer('rainfall_layer_1')) {
        map.setPaintProperty('rainfall_layer_1', 'raster-opacity', 0);
        map.setLayoutProperty('rainfall_layer_1', 'visibility', 'none');
      }
      // layer 2 should be invisble
      if (map.getLayer('rainfall_layer_2')) {
        map.setPaintProperty('rainfall_layer_2', 'raster-opacity', 0);
        map.setLayoutProperty('rainfall_layer_2', 'visibility', 'none');
      }

      // loading the first image before making anything visible
      try {
        await updateRainfallImage(map, steps[0]);
        await updateRunoffImage(map, steps[0], isOptimalMode);
        setIsSimulationPlaying(true);
      } catch (error) {
        console.error('Error starting simulation:', error);
      }
    }
  };

  useEffect(() => {
    if (catchment) {
      setViewMode('catchment');
    }
  }, [catchment]);

  useEffect(() => {
    if (viewMode === 'catchment') {
      setIsSimulationPlaying(false);
      setSimulationSelection(null);
      setSimulationSliderValue(0);
      setScenarioCsoEvents([]);

      if (map) {
        const source = map.getSource('rainfall-event-area') as maplibregl.GeoJSONSource;
        clearRainfallEventBoundarySource(source);

        // hide layer-1
        if (map.getLayer('rainfall_layer_1')) {
          map.setLayoutProperty('rainfall_layer_1', 'visibility', 'none');
        }
        // hide layer-2
        if (map.getLayer('rainfall_layer_2')) {
          map.setLayoutProperty('rainfall_layer_2', 'visibility', 'none');
        }
      }
    } else if (viewMode === 'simulation') {
      setCatchment(null);

      const stepSize = 100 / (steps.length - 1);
      const index = Math.round(simulationSliderValue / stepSize);

      const currentStep = steps[index];
      if (currentStep) {
        onSimulationStepChange(index);
      }
    }
  }, [viewMode, isOptimalMode]);

  useEffect(() => {
    setIsSimulationPlaying(false);
    setSimulationSliderValue(0);
  }, [simulationSelection]);

  useEffect(() => {
    if (!simulationSelection) {
      setScenarioCsoEvents([]);
      return;
    }

    if (isOptimalMode) {
      setScenarioCsoEvents([]);
    } else {
      setScenarioCsoEvents([
        { csoId: 37408, day: '24/02' },
        { csoId: 37045, day: '26/02' },
      ]);
    }
  }, [simulationSelection, isOptimalMode]);

  useEffect(() => {
    if (!simulationSelection && map) {
      const source = map.getSource('rainfall-event-area') as maplibregl.GeoJSONSource;
      if (source) {
        clearRainfallEventBoundarySource(source);
      }
    } else if (map && viewMode === 'simulation') {
      map.setCenter([-0.07964, 51.63385]);
      map.setZoom(13);
      const source = map.getSource('rainfall-event-area') as maplibregl.GeoJSONSource;
      source.setData(getRainfallEventArea());
    }
  }, [map, viewMode, simulationSelection, rainfallEventPolygon]);

  const onSimulationStepChange = async (step: number) => {
    if (viewMode === 'simulation') {
      const day = steps[step];
      setStepDay(day);

      if (map) {
        try {
          await updateRainfallImage(map, day);
          await updateRunoffImage(map, day, isOptimalMode);
        } catch (error) {
          console.error('Error updating rainfall image:', error);
        }
      }
    }
  };

  useEffect(() => {
    if (stepDay) {
      const activeCsos = scenarioCsoEvents
        .filter((event) => event.day === stepDay)
        .map((event) => event.csoId);
      setActiveCsoEvents(activeCsos);
    }
  }, [stepDay, isOptimalMode, scenarioCsoEvents]);

  useEffect(() => {
    setIsSimulationPlaying(false);
    setSimulationSliderValue(0);
  }, [isOptimalMode]);

  const findOptimalLocations = async () => {
    setViewMode('simulation');
    setIsOptimalMode(true);

    try {
      const data = await fetchSuggestedSudsData();
      setSuggestedSudsData(data);
    } catch (error) {
      console.error('Error fetching SUDS data:', error);
    }
  };

  const exitOptimalMode = () => {
    clearDrawer();
    setIsOptimalMode(false);
    setIsOptimalModeLoaded(false);
    setSuggestedSudsData(null);
  };

  return (
    <MapContext.Provider
      value={{
        map,
        mapContainer,
        catchment,
        simulationSelection,
        isSimulationPlaying,
        simulationSliderValue,
        isCatchmentLoading,
        isCatchmentSuccess,
        mapFilterPolygon,
        isOptimalMode,
        isOptimalModeLoaded,
        showRainfallLayer,
        activeCsoEvents,
        setCatchment,
        setSimulationSelection,
        setIsSimulationPlaying,
        setSimulationSliderValue,
        playSimulation,
        onSimulationStepChange,
        findOptimalLocations,
        setIsOptimalModeLoaded,
        exitOptimalMode,
        setShowRainfallLayer,
      }}
    >
      {children}
    </MapContext.Provider>
  );
}

export const useMapContext = () => {
  const context = useContext(MapContext);
  if (context === undefined) {
    throw new Error('useMapContext must be used within a MapContextProvider');
  }
  return context;
};
